import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Paper,
  Stack,
  Card,
  CardActionArea,
  CircularProgress,
  useTheme,
  alpha,
  TextField,
  InputAdornment
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import GroupsIcon from '@mui/icons-material/Groups'
import { apiGet } from '../../../../../generic/Api_Functions'
import DeviceVisibilityConfiguration from '../../../common/deviceVisibility/DeviceVisibilityConfiguration'
import { deduplicateDevicesById, deviceIdKey } from '../../../common/deviceVisibility/hiddenDeviceIds'

/**
/**
 * Configuration component for the Device Group widget.
 * Allows selecting a group from the list fetched from 'deviceGroups/simple'.
 */

const DeviceGroupConfiguration = ({ data, setData, t_ }) => {
  const theme = useTheme()
  const [groups, setGroups] = useState([])
  const [loading, setLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')

  // Load groups when the component mounts
  useEffect(() => {
    const fetchGroups = async () => {
      setLoading(true)
      try {
        const result = await apiGet('deviceGroups/simple')
        if (Array.isArray(result)) {
          setGroups(result)
        }
      } catch (error) {
        console.error('Error fetching device groups:', error)
      } finally {
        setLoading(false)
      }
    }
    fetchGroups()
  }, [])

  // Filter groups by search term
  const filteredGroups = groups.filter(group =>
    group.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    group.code?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const currentGroupId = data?.deviceGroupId
  const showPanel = data?.showPanel ?? true

  /**
   * ⚠️ Story 157, RN21/RN22 — the hidden list is emptied by the GESTURE of choosing a different
   * group here, and by nothing else. Re-picking the group that was already selected is a gesture
   * with no effect and must not destroy the configuration, and a `deviceGroupId` that changes
   * value for any other reason — a layout template arriving with its group AND its hidden list
   * together — is not this case at all. Reacting to the value instead of to the gesture would make
   * D2 cancel D1 and the hidden list would never travel in a template.
   */
  const handleSelectGroup = (group) => {
    if (deviceIdKey(group.id) === deviceIdKey(currentGroupId)) {
      return
    }
    setData({
      ...data,
      deviceGroupId: group.id,
      groupName: group.name, // Optional, for preview if desired
      hiddenDeviceIds: []
    })
  }

  const handleTogglePanel = (e) => {
    setData({ ...data, showPanel: e.target.checked })
  }

  const loadGroupDevices = async () => {
    // The lightweight group listing returns one row per device-group association and does NOT
    // deduplicate, unlike the grid: collapsing by identifier is what keeps the two columns a
    // partition of the offerable set (RN35, I3).
    const rows = await apiGet(`deviceGroupDevices/${currentGroupId}`)
    return deduplicateDevicesById(rows)
  }

  return (
    <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant='h6' fontWeight='bold' color='primary'>
        {t_('Select Device Group')}
      </Typography>

      <Stack direction='row' spacing={2}>

        <FormControlLabel
          control={
            <Switch
              checked={showPanel}
              onChange={handleTogglePanel}
              color='primary'
              size='small'
            />
          }
          label={t_('Show Details Panel')}
        />
      </Stack>

      <TextField
        fullWidth
        size='small'
        placeholder={t_('Search group...')}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position='start'>
              <SearchIcon color='action' />
            </InputAdornment>
          )
        }}
      />

      <Paper
        variant='outlined'
        sx={{
          flexGrow: 1,
          overflow: 'auto',
          maxHeight: '400px',
          bgcolor: alpha(theme.palette.background.paper, 0.5),
          borderRadius: 2,
          p: 1
        }}
      >
        {loading
          ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress size={24} />
            </Box>
            )
          : filteredGroups.length === 0
            ? (
              <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                <Typography variant='body2'>{t_('No groups found')}</Typography>
              </Box>
              )
            : (
              <Stack spacing={1}>
                {filteredGroups.map((group) => {
                  const isSelected = currentGroupId === group.id
                  return (
                    <Card
                      key={group.id}
                      variant={isSelected ? 'elevation' : 'outlined'}
                      sx={{
                        borderColor: isSelected ? 'primary.main' : 'divider',
                        bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.08) : 'background.paper',
                        transition: 'all 0.2s'
                      }}
                    >
                      <CardActionArea onClick={() => handleSelectGroup(group)} sx={{ p: 1 }}>
                        <Stack direction='row' alignItems='center' spacing={2}>
                          <Box sx={{ position: 'relative' }}>
                            <GroupsIcon color={isSelected ? 'primary' : 'action'} />
                            {isSelected && (
                              <CheckCircleIcon
                                color='primary'
                                sx={{
                                  position: 'absolute',
                                  bottom: -4,
                                  right: -4,
                                  fontSize: 14,
                                  bgcolor: 'background.paper',
                                  borderRadius: '50%'
                                }}
                              />
                            )}
                          </Box>
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography variant='subtitle2' fontWeight={isSelected ? 'bold' : 'medium'}>
                              {group.name}
                            </Typography>
                            <Typography variant='caption' color='text.secondary'>
                              {group.code}
                            </Typography>
                          </Box>
                        </Stack>
                      </CardActionArea>
                    </Card>
                  )
                })}
              </Stack>
              )}
      </Paper>

      {currentGroupId
        ? (
          <DeviceVisibilityConfiguration
            data={data}
            setData={setData}
            t_={t_}
            loadOfferableDevices={loadGroupDevices}
            offerableKey={String(currentGroupId)}
            emptyOfferableMessage='This group has no devices'
          />
          )
        : (
          <Typography variant='body2' color='text.secondary'>
            {t_('Select a device group first')}
          </Typography>
          )}
    </Box>
  )
}

DeviceGroupConfiguration.propTypes = {
  data: PropTypes.object,
  setData: PropTypes.func.isRequired,
  t_: PropTypes.func.isRequired
}

export default DeviceGroupConfiguration
